// #66, the results line above the grid: how many, for what, the active
// filters as chips you can remove one by one, and the sort on the end.
import { useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { Icon } from "@majourneys/icons";
import { SelectMenu, Chevron } from "@majourneys/ui";
import { getMoroccoNow, isRestaurantCuisine } from "@majourneys/shared";
import { CONTROL_EDGE, CONTROL_LIT } from "@majourneys/shared/ui";
import { menuLabels } from "../../../shared/ui/menu-labels";
import { useClientNow } from "../hooks/use-client-now";
import { OPEN_HOURS_HUBS, TONIGHT_HUBS, patchSearch, readHubWhen, removeFilterSearch } from "../lib/hub-when";
import { parseShowFilter } from "../api/activities-query";
import { formatBarDay } from "./hub-booking-bar";
import { isHubCollection, useCollectionLabels } from "../lib/hub-collections";

const NAVY = "#110E39";
const MUTED = "#5a5d77";
const CONTROL = { background: CONTROL_LIT, boxShadow: `0 0 0 1px ${CONTROL_EDGE}` } as const;
const RECOMMENDED = "recommended";

export interface ActiveFilterChip {
  /** The URL params the chip's × removes. */
  keys: string[];
  label: string;
}

/** The active filters on this hub, as removable chips. Day, time and guests are not filters. */
export function activeFilterChips(
  category: string | undefined,
  search: Record<string, unknown>,
  t: (key: string) => string,
  /** The hub's chip words; defaults to the restaurant keys. */
  collectionLabel: (id: string) => string = (id) => t(`restaurant_collections.${id}`),
): ActiveFilterChip[] {
  const when = readHubWhen(search);
  const out: ActiveFilterChip[] = [];
  if (category && OPEN_HOURS_HUBS.has(category)) {
    if (when.open) out.push({ keys: ["open"], label: t("explore.quick.open_now") });
    if (when.tonight && TONIGHT_HUBS.has(category)) out.push({ keys: ["tonight"], label: t("explore.quick.tables_tonight") });
  }
  const tag = typeof search.tag === "string" ? search.tag : undefined;
  if (isHubCollection(category, tag)) {
    out.push({ keys: ["tag"], label: collectionLabel(tag) });
  }
  const cuisine = typeof search.cuisine === "string" ? search.cuisine : undefined;
  if (category === "restaurants" && isRestaurantCuisine(cuisine)) {
    out.push({ keys: ["cuisine"], label: t(`restaurant_cuisines.${cuisine}`) });
  }
  if (parseShowFilter(search.show) === "all") out.push({ keys: ["show"], label: t("filter.show_all") });
  return out;
}

function addDays(iso: string, n: number): string {
  const d = new Date(`${iso}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}

export function HubResultsLine({
  category,
  total,
  lang,
  search,
  sortOptions,
}: {
  category: string;
  total: number;
  lang: string;
  search: Record<string, unknown>;
  /** The page's own sort options, already translated. "" is Recommended. */
  sortOptions: { value: string; label: string }[];
}) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const now = useClientNow();
  const when = readHubWhen(search);
  const collectionLabels = useCollectionLabels(category);

  // "12 restaurants": the category's own plural where the locale has one,
  // "12 places" elsewhere.
  const nounKey = `explore.results_noun.${category}`;
  const count = i18n.exists(nounKey, { count: total })
    ? t(nounKey, { count: total })
    : t("explore.place_count", { count: total });

  // "tonight at 20:30 for 2 guests", built from what the bar holds. No "with
  // a table" claim: the list still holds places with unknown hours and places
  // that are not bookable, so the line describes the search, not the result.
  // "Tonight" and "today" need the clock, so before mount the day is a date.
  const parts: string[] = [];
  if (OPEN_HOURS_HUBS.has(category) && (when.date || when.time || when.adults)) {
    const today = now ? getMoroccoNow(now).date : null;
    const day = when.date ?? (when.time ? today : null);
    if (day) {
      if (day === today) {
        parts.push(when.time && when.time >= "17:00" ? t("explore.results_ctx.tonight") : t("explore.results_ctx.today"));
      } else if (today && day === addDays(today, 1)) {
        parts.push(t("explore.results_ctx.tomorrow"));
      } else {
        parts.push(t("explore.results_ctx.on_day", { day: formatBarDay(day, lang) }));
      }
    }
    if (when.time) parts.push(t("explore.results_ctx.at_time", { time: when.time }));
    if (when.adults) parts.push(t("explore.results_ctx.for_guests", { count: when.adults }));
  }

  const chips = activeFilterChips(category, search, t, collectionLabels.label);
  const sortValue = typeof search.sort === "string" && search.sort ? search.sort : RECOMMENDED;
  const options = sortOptions.map((o) => ({ value: o.value || RECOMMENDED, label: o.label }));
  const sortLabel = options.find((o) => o.value === sortValue)?.label ?? options[0]?.label ?? "";

  return (
    <div className="flex flex-wrap items-center gap-2 pt-2" data-testid="hub-results-line">
      <span className="text-[14px] font-bold tabular-nums" style={{ color: NAVY }} aria-live="polite">
        {count}
      </span>
      {parts.length > 0 ? (
        <span className="text-[13px]" style={{ color: MUTED }} data-testid="hub-results-context">
          {parts.join(" ")}
        </span>
      ) : null}
      {chips.map((c) => (
        <span
          key={c.keys.join(",")}
          className="mj-pop-in inline-flex items-center gap-1 rounded-full py-0.5 pe-0.5 ps-2.5 text-[12px] font-semibold"
          style={{ ...CONTROL, color: NAVY }}
        >
          {c.label}
          <button
            type="button"
            aria-label={t("explore.results_remove", { label: c.label })}
            data-testid={`remove-filter-${c.keys[0]}`}
            onClick={() =>
              navigate({
                search: ((prev: Record<string, unknown>) => removeFilterSearch(prev, c.keys)) as never,
                resetScroll: false,
              })
            }
            className="grid h-6 w-6 place-items-center rounded-full hover:bg-[rgba(17,14,57,0.06)]"
            style={{ color: MUTED }}
          >
            <Icon name="ri-close-line" className="text-[14px]" aria-hidden="true" />
          </button>
        </span>
      ))}
      {options.length > 1 ? (
        <div className="ms-auto">
          <SelectMenu
            name="hub-results-sort"
            options={options}
            value={sortValue}
            onChange={(v) =>
              navigate({
                search: ((prev: Record<string, unknown>) =>
                  patchSearch(prev, { sort: v === RECOMMENDED ? undefined : v })) as never,
                resetScroll: false,
              })
            }
            labels={menuLabels(t, t("sort.label"))}
            side="end"
            width={220}
            renderTrigger={({ ref, open, buttonProps }) => (
              <button
                {...buttonProps}
                ref={ref}
                aria-label={`${t("sort.label")}, ${sortLabel}`}
                className="mj-press inline-flex min-h-[34px] items-center gap-1.5 rounded-full px-3 text-[12.5px] font-semibold"
                style={{ ...CONTROL, color: NAVY }}
              >
                <Icon name="ri-arrow-up-down-line" className="text-[13px]" aria-hidden="true" />
                {sortLabel}
                <Chevron open={open} size={13} />
              </button>
            )}
          />
        </div>
      ) : null}
    </div>
  );
}
